import { useState, useCallback, useContext, createContext } from 'react'
import {
  fetchAuthSession,
  getCurrentUser,
  signOut as signOutUser,
  signIn as signInUser,
} from 'aws-amplify/auth'
import { v4 as uuidv4 } from 'uuid'

import { useAppState } from './useAppState'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(false)
  const { dispatch } = useAppState()

  // Reset app state
  const resetAppState = useCallback(() => {
    dispatch({ type: 'reset-state' })
  }, [dispatch])

  const getAuthenticatedUser = useCallback(async () => {
    try {
      const { username, signInDetails } = await getCurrentUser()
      const { tokens: session } = await fetchAuthSession()

      dispatch({ type: 'set-me', payload: uuidv4() })

      const u = {
        username,
        session,
        authenticationFlowType: signInDetails.authFlowType,
      }

      setUser(u)

      return u
    } catch (err) {
      resetAppState()
      setUser(null)
      // throw err
    }
  }, [resetAppState, dispatch])

  const signIn = async (accessCode) => {
    return signInUser({ username: accessCode, password: accessCode })
      .then(async () => {
        // Set my identify
        const u = await getAuthenticatedUser()
        dispatch({ type: 'set-me', payload: uuidv4() })

        setUser(u)

        return u
      })
      .catch((err) => {
        resetAppState()
        setUser(null)
        throw err
      })
  }

  const signOut = async (callback) => {
    try {
      await signOutUser()
      setUser(null)

      if (callback) {
        callback()
      }
    } catch (err) {
      throw err
    } finally {
      resetAppState()
    }
  }

  const getGroup = (u) => {
    return u?.session?.accessToken?.payload?.['cognito:groups'] ?? false
  }

  const isOwnerCheck = (u) => {
    return (
      u?.session?.accessToken?.payload?.['cognito:groups'].includes('Owner') ??
      false
    )
  }

  const isHostCheck = (u) => {
    return (
      u?.session?.accessToken?.payload?.['cognito:groups'].includes('Host') ??
      false
    )
  }

  const isLeaderCheck = (u) => {
    return (
      u?.session?.accessToken?.payload?.['cognito:groups'].includes('Leader') ??
      false
    )
  }

  const isTeamCheck = (u) => {
    return (
      u?.session?.accessToken?.payload?.['cognito:groups'].includes('Team') ??
      false
    )
  }

  const getUserId = useCallback((u) => {
    return u?.session?.idToken?.payload?.['custom:userid'] ?? null
  }, [])

  /**
   * Get user's custom attribute
   */
  const getUserCustomAttribute = useCallback((u, attrName) => {
    return u?.session?.idToken?.payload?.[`custom:${attrName}`] ?? null
  }, [])

  const group = getGroup(user)
  const isOwner = isOwnerCheck(user)
  const isHost = isHostCheck(user)
  const isLeader = isLeaderCheck(user)
  const isTeam = isTeamCheck(user)
  const userId = user
    ? parseInt(user.session?.idToken?.payload?.['custom:userid'])
    : null

  return (
    <AuthContext.Provider
      value={{
        user,
        group,
        userId,
        isOwner,
        isHost,
        isLeader,
        isTeam,
        signIn,
        signOut,
        getAuthenticatedUser,
        getUserId,
        getUserCustomAttribute,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  return useContext(AuthContext)
}
